// The service level objectives (spec/21 §1), as data.
//
// The table in spec/21 §1 is the contract with a venue: a release that misses a gate does not ship.
// Copying those numbers into a dashboard by hand is how they drift, so they are here once and the
// SLO dashboard is generated from them — target line, unit and all.
//
// `metric` names an entry in the metric catalogue. Several of these SLOs are about features that
// do not exist yet; the catalogue says so, and the generated panel says so on its face rather than
// drawing a flat zero under a target line.
import { requireMetric } from "./metrics.js";
const CATALOGUE = [
    {
        id: 'api-availability',
        area: 'API availability',
        objective: '99.9% monthly (≤ ~43 min/month) for /v1 and the realtime gateway',
        targets: [{ kind: 'availability', ratio: 0.999, budgetMinutesPerMonth: 43 }],
        metric: 'http.server.request.duration',
        window: 'monthly',
    },
    {
        id: 'api-read-latency',
        area: 'API latency (reads)',
        objective: 'p50 < 100 ms, p95 < 400 ms, p99 < 800 ms for reads at the region',
        targets: [
            { kind: 'latency', quantile: 0.5, thresholdSeconds: 0.1 },
            { kind: 'latency', quantile: 0.95, thresholdSeconds: 0.4 },
            { kind: 'latency', quantile: 0.99, thresholdSeconds: 0.8 },
        ],
        metric: 'http.server.request.duration',
        window: 'rolling-28d',
    },
    {
        id: 'api-write-latency',
        area: 'API latency (writes)',
        objective: 'writes p95 < 600 ms',
        targets: [{ kind: 'latency', quantile: 0.95, thresholdSeconds: 0.6 }],
        metric: 'http.server.request.duration',
        window: 'rolling-28d',
    },
    {
        id: 'kiosk-sign-in',
        area: 'Sign-in to playable desktop (kiosk)',
        objective: '≤ 3 s p95 on venue broadband',
        targets: [{ kind: 'latency', quantile: 0.95, thresholdSeconds: 3 }],
        metric: 'clavian.session.sign_in.duration',
        window: 'rolling-28d',
    },
    {
        id: 'command-ack',
        area: 'Staff action → PC reacts (online)',
        objective: '≤ 1 s p95 (command ack)',
        targets: [{ kind: 'latency', quantile: 0.95, thresholdSeconds: 1 }],
        metric: 'clavian.realtime.command.ack.duration',
        window: 'rolling-28d',
    },
    {
        id: 'screen-lock',
        area: 'Screen lock after a kill/crash',
        objective: '≤ 2 s (cover ≤ 300 ms)',
        targets: [{ kind: 'latency', quantile: 0.95, thresholdSeconds: 2 }],
        // Measured on the station, in the locker's own tests and in the escape-test suite
        // (spec/21 §2, F-LCK-10). It is a release gate on a reference machine, not a fleet metric:
        // the platform never sees the milliseconds between a kill and a cover.
        metric: null,
        window: 'per-release',
    },
    {
        id: 'realtime-delivery',
        area: 'Realtime delivery (event → client)',
        objective: '≤ 500 ms p95',
        targets: [{ kind: 'latency', quantile: 0.95, thresholdSeconds: 0.5 }],
        metric: 'clavian.realtime.delivery.duration',
        window: 'rolling-28d',
    },
    {
        id: 'play-time-integrity',
        area: 'Play time lost or invented',
        objective: 'Zero, provable from the ledger and segments',
        targets: [{ kind: 'zero', of: 'minutes of play time that the ledger and the segments disagree about' }],
        metric: 'clavian.ledger.imbalance',
        window: 'monthly',
    },
    {
        id: 'payment-webhook',
        area: 'Payment webhook → order paid',
        objective: '≤ 5 s p95 after the provider sends it',
        targets: [{ kind: 'latency', quantile: 0.95, thresholdSeconds: 5 }],
        metric: 'clavian.payment.webhook.latency',
        window: 'rolling-28d',
    },
    {
        id: 'data-durability',
        area: 'Data durability',
        objective: 'Postgres PITR; RPO ≤ 5 min, RTO ≤ 1 h',
        targets: [
            { kind: 'latency', quantile: 1, thresholdSeconds: 300 },
            { kind: 'latency', quantile: 1, thresholdSeconds: 3600 },
        ],
        // Proved by the quarterly restore drill (spec/21 §8), not by a time series. The panel on the
        // SLO dashboard shows when the last drill ran and what it measured, which is the honest
        // rendering of an objective nothing emits continuously.
        metric: null,
        window: 'monthly',
    },
];
export const SLOS = CATALOGUE;
const BY_ID = new Map(SLOS.map((slo) => [slo.id, slo]));
export function findSlo(id) {
    return BY_ID.get(id);
}
/** The metric behind an SLO, checked against the catalogue. `null` for the two that nothing emits. */
export function sloMetric(slo) {
    return slo.metric === null ? null : requireMetric(slo.metric);
}
//# sourceMappingURL=slo.js.map